import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";

/**
 * /demo — gallery of all 23 template-specific demo invitations.
 * Each card opens its OWN unique demo (`/i/demo-<template_id>`) with different
 * couple names, venue, hero image and palette so visitors can visualize their
 * own wedding in every theme. Replaces the old single-demo redirect.
 */

const GOLD = "#D4AF37";
const GOLD_SOFT = "#F4E5BC";
const INK = "#0A0908";
const INK_2 = "#15120E";
const LINE = "#2a2620";

function thumb(url) {
  if (!url) return url;
  if (!url.includes("images.unsplash.com")) return url;
  const base = url.split("?")[0];
  return `${base}?w=900&q=75&auto=format&fm=webp&fit=crop`;
}

export default function Demo() {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/demos").then((r) => { setDemos(r.data || []); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: INK, color: GOLD_SOFT, fontFamily: "Georgia, 'Playfair Display', serif" }} data-testid="demo-gallery">
      <SEO
        title="23 Live Wedding Website Demos — See Before You Build"
        description="Explore 23 unique live wedding website demos. Royal Indian, Bengali, Punjabi, Christian, Beach, Floral — each demo has different couple, venue and palette. Tap to view full live site."
        path="/demo"
      />
      {/* Top nav */}
      <nav className="fixed top-0 inset-x-0 z-40 px-6 md:px-10 py-4 flex items-center justify-between" style={{ background: "rgba(10,9,8,0.78)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${LINE}` }}>
        <Link to="/" className="font-serif-display text-xl" data-testid="demo-nav-logo" style={{ color: GOLD_SOFT }}>Weddinsite</Link>
        <div className="flex items-center gap-3">
          <Link to="/templates" className="text-xs uppercase tracking-[0.3em] hidden sm:inline" style={{ color: GOLD_SOFT, opacity: 0.75 }}>Templates</Link>
          <Link to="/signup" data-testid="demo-nav-cta" className="text-xs uppercase tracking-[0.25em] px-4 py-2.5" style={{ background: GOLD, color: INK }}>Start Free</Link>
        </div>
      </nav>

      {/* Hero header */}
      <section className="pt-28 md:pt-36 pb-14 px-6 md:px-10 text-center relative">
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div style={{ width: "min(900px, 90vw)", height: "min(900px, 90vw)", background: `radial-gradient(circle, ${GOLD}1a 0%, transparent 60%)` }} />
        </div>
        <div className="relative max-w-4xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: GOLD }}>The Demo Gallery</div>
          <h1 className="font-serif-display font-light leading-[1.0]" style={{ fontSize: "clamp(36px, 6vw, 72px)" }} data-testid="demo-hero-title">
            {demos.length || 23} live demos. <em style={{ color: GOLD }}>Pick your favorite.</em>
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#a8a098" }}>
            Each demo is a complete wedding website — different couple, different city, different palette.
            Tap any card to experience it exactly as your guests will.
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 md:px-10 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] animate-pulse" style={{ background: INK_2 }} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {demos.map((d, i) => (
                <a
                  key={d.template_id}
                  href={`/i/${d.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`demo-card-${d.template_id}`}
                  className="group relative block overflow-hidden aspect-[3/4] transition-transform hover:-translate-y-1"
                  style={{ background: INK_2, border: `1px solid ${LINE}` }}
                >
                  <img
                    src={thumb(d.cover)}
                    alt={`${d.name} demo — ${d.couple}`}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/95 via-black/55 to-transparent">
                    <div className="font-serif-display text-xl md:text-2xl leading-tight" style={{ color: GOLD_SOFT }}>{d.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] mt-1.5" style={{ color: GOLD }}>{d.couple}</div>
                    <div className="mt-3 text-[11px] uppercase tracking-[0.25em] flex items-center gap-1.5" style={{ color: GOLD_SOFT, opacity: 0.85 }}>
                      <span>View live demo</span>
                      <span style={{ color: GOLD }}>→</span>
                    </div>
                  </div>
                  {/* Color swatch chips top-right */}
                  <div className="absolute top-3 right-3 flex gap-1.5">
                    <span className="w-3 h-3 rounded-full border" style={{ background: d.palette?.bg, borderColor: "rgba(255,255,255,0.2)" }} />
                    <span className="w-3 h-3 rounded-full border" style={{ background: d.palette?.accent, borderColor: "rgba(255,255,255,0.2)" }} />
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-16 md:mt-20 text-center" data-testid="demo-bottom-cta">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: GOLD }}>Ready?</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light">Build <em style={{ color: GOLD }}>your</em> wedding website — free.</h2>
            <p className="mt-4 text-sm md:text-base" style={{ color: "#a8a098" }}>Pick any template, customize freely. Pay only ₹999 when you&apos;re ready to publish.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/signup" data-testid="demo-cta-signup" className="inline-block px-8 py-4 text-xs uppercase tracking-[0.3em] font-semibold" style={{ background: GOLD, color: INK }}>
                Start Free →
              </Link>
              <Link to="/templates" data-testid="demo-cta-templates" className="inline-block px-8 py-4 text-xs uppercase tracking-[0.3em] border hover:bg-white/5" style={{ borderColor: GOLD, color: GOLD_SOFT }}>
                Browse all templates
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
